import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatAngularModule } from '../../../modules/mat-angular.module';
import { ScoreCardsComponent } from '../../score-cards/score-cards.component';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { PieComponent } from '../../pie/pie.component';

@Component({
  selector: 'app-common-roi-comparision',
  standalone: true,
  imports: [
    CommonModule,
    MatAngularModule,
    // ScoreCardsComponent,
    NgxChartsModule,
    PieComponent,
  ],
  templateUrl: './common-roi-comparision.component.html',
  styleUrl: './common-roi-comparision.component.scss'
})
export class CommonRoiComparisionComponent implements OnChanges {

  @Input() tabData: any = null;
  @Input() config: any = null;
  @Input() evidenceSources: any[] = [];
  @Input() recommendations: any[] = [];

  // Transformed data for Score Cards component
  transformedUniversities: any[] = [];
  availableMetrics: string[] = [];

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabData'] && this.tabData) {
      this.transformData();
    }
  }

  // Method to generate pie chart data for a specific university and metric
  getPieChartData(university: any, metric: string): any[] {
    const score = university[metric] || 0;
    const maxScore = 100; // Assuming max score is 100

    return [
      {
        name: metric,
        value: score
      },
      {
        name: 'Remaining',
        value: maxScore - score
      }
    ];
  }

  // Method to handle pie chart selection events
  onPieChartSelect(event: any): void {
    // // console.log('Pie chart selected:', event);
  }

  private transformData(): void {
    if (!this.tabData) {
      this.transformedUniversities = [];
      this.availableMetrics = [];
      return;
    }

    // // console.log('Transforming area comparison data:', this.tabData);

    // Handle the new API structure from the selected file
    if (this.tabData.universities && Array.isArray(this.tabData.universities)) {
      // New structure: universities array with scores object
      this.transformedUniversities = this.tabData.universities.map((university: any) => {
        const transformedUni: any = {
          name: university.name || 'Unknown University'
        };

        // Extract metrics from scores object
        if (university.scores && typeof university.scores === 'object') {
          Object.keys(university.scores).forEach(metricName => {
            const metricValue = university.scores[metricName];
            if (typeof metricValue === 'number') {
              transformedUni[metricName] = metricValue;
              // Add to available metrics if not already present
              if (!this.availableMetrics.includes(metricName)) {
                this.availableMetrics.push(metricName);
              }
            }
          });
        }

        return transformedUni;
      });
    }
    // Handle chart_data structure (fallback for old format)
    else if (this.tabData.chart_data && this.tabData.chart_data.data && Array.isArray(this.tabData.chart_data.data)) {
      this.transformedUniversities = this.tabData.chart_data.data.map((university: any) => {
        const transformedUni: any = {
          name: university.name || 'Unknown University'
        };

        // Extract metrics from series
        if (university.series && Array.isArray(university.series)) {
          university.series.forEach((metric: any) => {
            if (metric.name && metric.value !== undefined) {
              transformedUni[metric.name] = metric.value;
              if (!this.availableMetrics.includes(metric.name)) {
                this.availableMetrics.push(metric.name);
              }
            }
          });
        }

        return transformedUni;
      });
    }
    // Handle legacy data structure
    else if (this.tabData.data && Array.isArray(this.tabData.data)) {
      // Find the first data item with chart_data
      let chartData = null;
      for (const dataItem of this.tabData.data) {
        if (dataItem.scores && dataItem.scores.chart_data) {
          chartData = dataItem.scores.chart_data;
          break;
        }
      }

      if (chartData && Array.isArray(chartData)) {
        this.transformedUniversities = chartData.map((university: any) => {
          const transformedUni: any = {
            name: university.name || university.university || 'Unknown University'
          };

          // Extract metrics from series
          if (university.series && Array.isArray(university.series)) {
            university.series.forEach((metric: any) => {
              if (metric.name && metric.value !== undefined) {
                transformedUni[metric.name] = metric.value;
                if (!this.availableMetrics.includes(metric.name)) {
                  this.availableMetrics.push(metric.name);
                }
              }
            });
          }

          return transformedUni;
        });
      }
    }
    // Handle direct chart_data access
    else if (this.tabData.chart_data && Array.isArray(this.tabData.chart_data)) {
      this.transformedUniversities = this.tabData.chart_data.map((university: any) => {
        const transformedUni: any = {
          name: university.name || university.university || 'Unknown University'
        };

        // Extract metrics from series
        if (university.series && Array.isArray(university.series)) {
          university.series.forEach((metric: any) => {
            if (metric.name && metric.value !== undefined) {
              transformedUni[metric.name] = metric.value;
              if (!this.availableMetrics.includes(metric.name)) {
                this.availableMetrics.push(metric.name);
              }
            }
          });
        }

        return transformedUni;
      });
    }
    // Handle comparison_data fallback
    else if (this.tabData.comparison_data && Array.isArray(this.tabData.comparison_data)) {
      this.transformedUniversities = this.tabData.comparison_data.map((university: any) => {
        const transformedUni: any = {
          name: university.name || university.university || 'Unknown University'
        };

        // Extract metrics from series or direct properties
        if (university.series && Array.isArray(university.series)) {
          university.series.forEach((metric: any) => {
            if (metric.name && metric.value !== undefined) {
              transformedUni[metric.name] = metric.value;
              if (!this.availableMetrics.includes(metric.name)) {
                this.availableMetrics.push(metric.name);
              }
            }
          });
        }

        // Also check for direct properties
        Object.keys(university).forEach(key => {
          if (key !== 'name' && key !== 'university' && key !== 'series' &&
            typeof university[key] === 'number') {
            transformedUni[key] = university[key];
            if (!this.availableMetrics.includes(key)) {
              this.availableMetrics.push(key);
            }
          }
        });

        return transformedUni;
      });
    }
    // Final fallback: use the data as-is if it's already in the right format
    else if (Array.isArray(this.tabData)) {
      this.transformedUniversities = this.tabData;
    }

    // // console.log('Transformed universities for Score Cards:', this.transformedUniversities);
    // // console.log('Available metrics:', this.availableMetrics);
  }

  getChartMetrics(): string[] {
    // Use available metrics if we have them, otherwise fall back to config
    if (this.availableMetrics.length > 0) {
      return this.availableMetrics.slice(0, 6); // Limit to 6 metrics
    }
    return this.config?.chartMetrics || [];
  }

  getMaxEntities(): number {
    return this.config?.maxEntities || 5;
  }

  getUniversitiesForScoreCards(): any[] {
    return this.transformedUniversities;
  }

  getEvidenceTooltip(evidence: any): string {
    if (!evidence) return '';

    let tooltip = '';
    if (evidence.category) tooltip += `Category: ${evidence.category}\n`;
    if (evidence.confidence_score && typeof evidence.confidence_score === 'number') {
      tooltip += `Confidence: ${Math.round(evidence.confidence_score * 100)}%\n`;
    }
    if (evidence.snippet) tooltip += `Snippet: ${evidence.snippet}\n`;
    if (evidence.url) tooltip += `\nClick to view source`;

    return tooltip.trim();
  }

  openSourceLink(url: string): void {
    if (url && url.trim() !== '' && url !== '#') {
      // Open the link in a new tab
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // Show a message if no URL is available
      // console.log('No source URL available for this item');
    }
  }
}
